import React from 'react';
import { FiX } from 'react-icons/fi';
import p from '../../pages/Pages.module.css';

const STATUSES = { 1: 'pending', 2: 'started', 3: 'finished', 4: 'draft', 5: 'first_half_done' };
const BADGE_MAP = { pending: p.badgePending, started: p.badgeStarted, finished: p.badgeFinished, draft: p.badgeDraft, first_half_done: p.badgeStarted };

export function getSideLabel(team) {
  const raw = team.side;
  if (raw === 1 || raw === 'Home' || raw === 'home') return 'Home';
  if (raw === 2 || raw === 'Away' || raw === 'away') return 'Away';
  return String(raw);
}

export function formatMatchTime(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  const pad = (n) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function formatQuota(v) {
  return (v === null || v === undefined) ? '—' : Number(v).toFixed(2);
}

/**
 * MatchCard — reusable list item for matches/clasicos.
 * Props:
 *  - match: match object
 *  - onTeamClick: (matchId, team) => void (for clasico creation)
 *  - onItemClick: (match) => void (for detail modals)
 *  - selectedTeams: [{ matchId, teamId }] for highlight
 *  - onDelete: (match) => void  Optional. Renders a small X button (with stopPropagation)
 *              on the right edge so callers can confirm + delete the match.
 */
export default function MatchCard({ match, onTeamClick, onItemClick, selectedTeams = [], onDelete }) {
  const homeTeam = match.teams?.find(t => getSideLabel(t) === 'Home');
  const awayTeam = match.teams?.find(t => getSideLabel(t) === 'Away');
  const statusCode = STATUSES[match.statusId] || null;

  const isSelected = (team) =>
    team && selectedTeams.some(s => s.matchId === match.id && s.teamId === team.id);

  const handleTeamClick = (e, team) => {
    if (onTeamClick && team) {
      e.stopPropagation();
      onTeamClick(match.id, team);
    }
  };

  return (
    <div
      className={`${p.card} ${onItemClick ? p.cardClickable : ''}`}
      onClick={() => onItemClick?.(match)}
      style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px' }}
    >
      <div
        style={{ flex: 1, cursor: onTeamClick ? 'pointer' : 'default', padding: '4px 8px', borderRadius: 6, background: isSelected(homeTeam) ? 'var(--bg-active)' : 'transparent', transition: 'background 0.15s' }}
        onClick={(e) => handleTeamClick(e, homeTeam)}
      >
        <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>{homeTeam?.name || '—'}</div>
        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Home</div>
      </div>

      <div style={{ textAlign: 'center', minWidth: 100 }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{formatMatchTime(match.matchTime)}</div>
        {statusCode && (
          <span className={`${p.badge} ${BADGE_MAP[statusCode] || ''}`} style={{ marginTop: 4 }}>
            {statusCode}
          </span>
        )}
      </div>

      <div
        style={{ flex: 1, textAlign: 'right', cursor: onTeamClick ? 'pointer' : 'default', padding: '4px 8px', borderRadius: 6, background: isSelected(awayTeam) ? 'var(--bg-active)' : 'transparent', transition: 'background 0.15s' }}
        onClick={(e) => handleTeamClick(e, awayTeam)}
      >
        <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>{awayTeam?.name || '—'}</div>
        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Away</div>
      </div>

      {onDelete && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(match); }}
          title="Delete match"
          style={{
            background: 'none', border: '1px solid rgba(224,108,117,0.25)', borderRadius: 6,
            padding: '4px 6px', cursor: 'pointer', color: '#e06c75', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <FiX size={14} />
        </button>
      )}
    </div>
  );
}

